import {
  Box,
  Card,
  CardActionArea,
  Chip,
  GridLegacy as Grid,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CopyableTypography from "components/Typography/CopyableTypography";
import { JobStatus } from "generated-graphql/graphql";
import { getJobStatusColor } from "routes/Staff/Jobs/utils";
import { prettyPrintDateTime, relativeLogTime } from "util/dates";

import type { ReportJob } from "./types";

import { prettyPrintWebAutomationAction } from "./prettyPrintWebAutomationAction";

export interface JobListPaneProps {
  jobs: ReportJob[];
  selectedJob: ReportJob | null;
  onSelectJob: (job: ReportJob) => void;
}

function JobCard({
  job,
  isSelected,
  onSelect,
}: {
  job: ReportJob;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const theme = useTheme();
  const statusColor = getJobStatusColor({
    jobStatus: job.status as JobStatus,
    theme,
  });
  return (
    <Card
      variant={isSelected ? "elevation" : "outlined"}
      elevation={isSelected ? 2 : 0}
      sx={{
        backgroundColor: isSelected ? theme.palette.grey[50] : undefined,
        borderColor: isSelected ? theme.palette.primary.main : theme.palette.divider,
        borderWidth: isSelected ? 2 : 1,
      }}
    >
      <CardActionArea onClick={onSelect} sx={{ display: "block" }}>
        <Stack spacing={0.75} sx={{ p: 1.5 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            sx={{ minWidth: 0 }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
              }}
            >
              {prettyPrintWebAutomationAction(job.action)}
            </Typography>
            <Chip
              size="small"
              label={job.status}
              sx={{
                backgroundColor: statusColor.main,
                color: statusColor.contrastText,
                flexShrink: 0,
              }}
            />
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <CopyableTypography
              variant="caption"
              sx={{ color: "text.secondary" }}
              text={job.id}
              displayText={`Job ID: ${job.id.slice(0, 6)}`}
            />
            <Tooltip title={prettyPrintDateTime(job.createdAt)}>
              <Typography variant="caption" color="text.secondary">
                Created: {relativeLogTime(job.createdAt)}
              </Typography>
            </Tooltip>
            <Tooltip title={prettyPrintDateTime(job.updatedAt)}>
              <Typography variant="caption" color="text.secondary">
                Updated: {relativeLogTime(job.updatedAt)}
              </Typography>
            </Tooltip>
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

export function JobListPane({ jobs, selectedJob, onSelectJob }: JobListPaneProps) {
  const theme = useTheme();

  return (
    <Grid
      item
      xs={12}
      md={4}
      sx={{
        borderRight: (t) => `solid 1px ${t.palette.divider}`,
        overflowX: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ p: theme.spacing(2), flex: 1, overflow: "auto" }}>
        <Typography variant="h6" sx={{ mb: 1.5 }}>
          Jobs
        </Typography>
        <Stack spacing={1}>
          {jobs.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              isSelected={selectedJob?.id === job.id}
              onSelect={() => onSelectJob(job)}
            />
          ))}
        </Stack>
      </Box>
    </Grid>
  );
}
